import React from "react";
import { H2, Paragraph, Span } from "@singlestore/fusion/components/typography";

import { Link } from "components/Link/Link";

import { insertBetween } from "utils/general";

import "./Footer.scss";

type FooterContent = {
    text: React.ReactNode | string;
    href?: string;
};

type FooterContentOrSpacer = FooterContent | { spacer: true };

type FooterLink = FooterContent & {
    href: string;
};

type FooterColumnType = {
    name: string;
    links: Array<FooterLink>;
};

const FOOTER_SMALL_CONTENT: Array<FooterContent> = [
    {
        text: <>&copy; SingleStore</>,
    },
    {
        text: "Privacy Policy",
        href: "https://www.singlestore.com/privacy-notice/",
    },
    {
        text: "Terms of Service",
        href: "https://www.singlestore.com/terms/",
    },
    {
        text: "Legal Terms and Conditions",
        href: "https://www.singlestore.com/legal/",
    },
];

const FOOTER_COLUMNS: Array<FooterColumnType> = [
    {
        name: "Product",
        links: [
            {
                text: "Product Overview",
                href: "https://www.singlestore.com/product-overview/",
            },
            {
                text: "Request a Demo",
                href: "https://www.singlestore.com/demo/",
            },
            {
                text: "Pricing",
                href: "https://www.singlestore.com/pricing/",
            },
            {
                text: "Support",
                href: "https://www.singlestore.com/support/",
            },
            {
                text: "Status",
                href: "https://status.singlestore.com/",
            },
        ],
    },
    {
        name: "Community",
        links: [
            {
                text: "Forums",
                href: "https://www.singlestore.com/forum/",
            },
            {
                text: "Spaces",
                href: "https://www.singlestore.com/spaces/",
            },
            {
                text: "Events",
                href: "https://www.singlestore.com/events/",
            },
            {
                text: "Training",
                href: "https://www.singlestore.com/training/",
            },
        ],
    },
    {
        name: "Company",
        links: [
            {
                text: "Contact Us",
                href: "https://www.singlestore.com/contact/",
            },
            {
                text: "Blog",
                href: "https://www.singlestore.com/blog/",
            },
            {
                text: "Careers",
                href: "https://www.singlestore.com/careers/",
            },
            {
                text: "Partners",
                href: "https://www.singlestore.com/partners/",
            },
        ],
    },
];

function FooterSmall() {
    const footerListItems = insertBetween<FooterContentOrSpacer>(
        { spacer: true },
        FOOTER_SMALL_CONTENT
    );

    return (
        <div className="footer-small-container">
            <ul className="links-list">
                {footerListItems.map((content, index) => {
                    if ("spacer" in content && content.spacer) {
                        return (
                            <li
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                aria-hidden="true"
                            >
                                <Paragraph variant="body-2">|</Paragraph>
                            </li>
                        );
                    }

                    if ("href" in content && content.href) {
                        return (
                            <li key={content.href}>
                                <Link
                                    to={content.href}
                                    type="secondary"
                                    variant="body-2"
                                >
                                    {content.text}
                                </Link>
                            </li>
                        );
                    }

                    return (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>
                            <Span variant="body-2">
                                {(content as FooterContent).text}
                            </Span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

type FooterColumnProps = {
    column: FooterColumnType;
};

function FooterColumn({ column }: FooterColumnProps) {
    return (
        <div className="links-column">
            <H2 variant="heading-2" className="column-header">
                {column.name}
            </H2>

            <ul className="links-list">
                {column.links.map((link) => {
                    return (
                        <li key={link.href} className="link-list-item">
                            <Link
                                to={link.href}
                                type="secondary"
                                variant="body-2"
                            >
                                {link.text}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export function Footer() {
    return (
        <footer className="footer">
            <div className="links-container">
                {FOOTER_COLUMNS.map((column) => {
                    return <FooterColumn key={column.name} column={column} />;
                })}
            </div>

            <Paragraph
                variant="annotation-2"
                textAlign="center"
                my="3x"
                px="2x"
            >
                This site is protected by reCAPTCHA and the Google{" "}
                <Link to="https://policies.google.com/privacy">
                    Privacy Policy
                </Link>{" "}
                and{" "}
                <Link to="https://policies.google.com/terms">
                    Terms of Service
                </Link>{" "}
                apply.
            </Paragraph>

            <FooterSmall />
        </footer>
    );
}
